import {
    AddItemsToProjectParams,
    AddItemToProjectAIParams,
    AddItemToProjectParams,
    CreateEmptyProjectParams,
    CreateProjectAIParams,
    CreateProjectParams,
    DeleteProjectParams,
    GetItemFromProjectParams,
    GetProjectParams,
    GetProjectsParams,
    RemoveItemFromProjectParams,
    UpdateAudioAINameParams,
    UpdateAudioAIPositionParams,
    UpdateItemFromProjectParams,
    UpdateProjectParams,
    GetProjectByTypesParams,
    RemoveItemFromProjectByTypeParams,
    GetAIWaitingByTypeParams,
    GetBlogAIWaitingParams,
    GetCartelAIWaitingParams,
    GetNewsletterAIWaitingParams,
    GetWebsiteAIWaitingParams,
    GetPressAIWaitingParams,
    CreateProjectCartelParams,
    CreateProjectNewsletterParams,
    GetDraftsParams,
    UpdateDraftItemParams,
    DeleteDraftItemParams,
} from "../types/requestInputs";
import {
    CreateEmptyProjectResponse,
    GetNewsletterAIWaitingResponse,
    GetCartelAIWaitingResponse,
    GetItemFromProjectResponse,
    GetProjectResponse,
    GetProjectsResponse,
    GetProjectByTypesResponse,
    GetAIWaitingByTypeResponse,
    GetDraftsResponse,
} from "../types/requestsOutputs";
import { MessageResponse } from "../types/types";
import { fetchAPI } from "./utils";

// Get all projects
export const getProjects = async ({ token, itemType }: GetProjectsParams): Promise<GetProjectsResponse> => {
    const queryParams = itemType ? `?itemType=${itemType}` : "";
    return fetchAPI<GetProjectsResponse>(`/get-projects${queryParams}`, "GET", undefined, token);
};

// Get a specific project by ID
export const getProject = async ({ token, projectId }: GetProjectParams): Promise<GetProjectResponse> => {
    return fetchAPI<GetProjectResponse>("/get-project", "POST", { projectId }, token);
};

// Get project by specific types
export const getProjectByTypes = async ({
    token,
    projectId,
    targetTypes,
}: GetProjectByTypesParams): Promise<GetProjectByTypesResponse> => {
    return fetchAPI<GetProjectByTypesResponse>("/get-project-by-types", "POST", { projectId, targetTypes }, token);
};

// Drafts (Brouillon): AI content not assigned to a project. Owner only.
export const getDrafts = async ({
    token,
    itemType,
    page = 1,
    limit = 20,
    language,
    withText,
    withAudio,
    query,
}: GetDraftsParams): Promise<GetDraftsResponse> => {
    return fetchAPI<GetDraftsResponse>(
        "/get-drafts",
        "POST",
        { itemType, page, limit, language, withText, withAudio, query },
        token,
    );
};

export const updateDraftItem = async ({
    token,
    itemId,
    itemType,
    language,
    title,
    text,
    audioUrl,
    targetProjectAIId,
}: UpdateDraftItemParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/update-draft-item",
        "POST",
        { itemId, itemType, language, title, text, audioUrl, targetProjectAIId },
        token,
    );
};

export const deleteDraftItem = async ({ token, itemId, itemType }: DeleteDraftItemParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-draft-item", "POST", { itemId, itemType }, token);
};

// Create a new project
export const createProject = async ({ token, projectData }: CreateProjectParams): Promise<String> => {
    return fetchAPI<String>("/create-project", "POST", projectData, token);
};

// Update a project's name
export const updateProject = async ({ token, projectId, name }: UpdateProjectParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-project", "POST", { projectId, name }, token);
};

// Delete a project
export const deleteProject = async ({ token, projectId }: DeleteProjectParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-project", "POST", { projectId }, token);
};

// Create an empty project
export const createEmptyProject = async ({
    token,
    name,
}: CreateEmptyProjectParams): Promise<CreateEmptyProjectResponse> => {
    return fetchAPI<CreateEmptyProjectResponse>("/create-empty-project", "POST", { name }, token);
};

export const createProjectAI = async ({
    token,
    name,
    audioAIId,
}: CreateProjectAIParams): Promise<CreateEmptyProjectResponse> => {
    return fetchAPI<CreateEmptyProjectResponse>("/create-project-ai", "POST", { name, audioAIId }, token);
};

export const createProjectCartel = async ({ token, name, cartelAIId }: CreateProjectCartelParams): Promise<String> => {
    return fetchAPI<String>("/create-project-cartel", "POST", { name, cartelAIId }, token);
};

export const createProjectNewsletter = async ({
    token,
    name,
    newsletterAIId,
}: CreateProjectNewsletterParams): Promise<String> => {
    return fetchAPI<String>("/create-project-newsletter", "POST", { name, newsletterAIId }, token);
};

// Update AI audio position in projects
export const updateAudioAIPositionInProjects = async ({
    token,
    modifiedData,
}: UpdateAudioAIPositionParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/renumber-stories-projects", "POST", { modifiedData }, token);
};

// Update AI audio name
export const updateAudioAIName = async ({
    token,
    audioAIId,
    name,
}: UpdateAudioAINameParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/update-audioai-name", "POST", { audioAIId, name }, token);
};

// Add a single item to a project
export const addItemToProject = async ({
    token,
    projectId,
    text,
    audioUrl,
    voiceAI,
    language,
    contentName,
    isDialog,
    voiceAISecond,
}: AddItemToProjectParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/add-item-to-project",
        "POST",
        { projectId, text, audioUrl, voiceAI, language, contentName, isDialog, voiceAISecond },
        token,
    );
};

// Add multiple items to a project
export const addItemsToProject = async ({
    token,
    projectId,
    language,
    items,
    voiceLabel,
}: AddItemsToProjectParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/add-items-to-project",
        "POST",
        { projectId, language, items, voiceLabel },
        token,
    );
};

// Add AI-generated item to a project
export const addItemToProjectAI = async ({
    token,
    projectId,
    itemId,
    itemType,
}: AddItemToProjectAIParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/add-item-to-project-ai", "POST", { projectId, itemId, itemType }, token);
};

// Get a specific item from a project
export const getItemFromProject = async ({
    token,
    projectId,
    audioAIId,
}: GetItemFromProjectParams): Promise<GetItemFromProjectResponse> => {
    return fetchAPI<GetItemFromProjectResponse>("/get-project-item-info", "POST", { projectId, audioAIId }, token);
};

// Update an item inside a project
export const updateItemFromProject = async ({
    token,
    projectId,
    audioAIId,
    language,
    title,
    text,
    audioUrl,
    targetProjectAIId,
    voiceLabel,
}: UpdateItemFromProjectParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/update-project-item-info",
        "POST",
        {
            projectId,
            itemId: audioAIId,
            itemType: "audio",
            language,
            title,
            text,
            audioUrl,
            targetProjectAIId,
            voiceLabel,
        },
        token,
    );
};

// Remove an item from a project
export const removeItemFromProject = async ({
    token,
    projectId,
    audioAIId,
}: RemoveItemFromProjectParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/remove-item-from-project",
        "POST",
        { projectId, itemId: audioAIId, itemType: "audio" },
        token,
    );
};

// Remove item from project by type
export const removeItemFromProjectByType = async ({
    token,
    projectId,
    itemId,
    itemType,
}: RemoveItemFromProjectByTypeParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/remove-item-from-project", "POST", { projectId, itemId, itemType }, token);
};

// Soft-delete an item from a project (moves it to the trash, restorable).
export const deleteItemFromProject = async ({
    token,
    projectId,
    itemId,
    itemType,
}: RemoveItemFromProjectByTypeParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/delete-item-from-project", "POST", { projectId, itemId, itemType }, token);
};

// Update cartel item in project
export const updateCartelItemFromProject = async ({
    token,
    projectId,
    itemId,
    language,
    title,
    text,
}: {
    token: string;
    projectId: string;
    itemId: string;
    language: string;
    title: string;
    text: string;
}): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/update-project-item-info",
        "POST",
        { projectId, itemId, itemType: "cartel", language, title, text },
        token,
    );
};

// Update newsletter item in project
export const updateNewsletterItemFromProject = async ({
    token,
    projectId,
    itemId,
    language,
    title,
    text,
}: {
    token: string;
    projectId: string;
    itemId: string;
    language: string;
    title: string;
    text: string;
}): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/update-project-item-info",
        "POST",
        { projectId, itemId, itemType: "newsletter", language, title, text },
        token,
    );
};

// Universal AI waiting status
export const getAIWaitingByType = async ({
    token,
    contentId,
    contentType,
}: GetAIWaitingByTypeParams): Promise<GetAIWaitingByTypeResponse> => {
    return fetchAPI<GetAIWaitingByTypeResponse>("/get-ai-waiting-by-type", "POST", { contentId, contentType }, token);
};

// Type-specific waiting status endpoints
export const getBlogAIWaiting = async ({
    token,
    blogAIId,
}: GetBlogAIWaitingParams): Promise<GetAIWaitingByTypeResponse> => {
    return fetchAPI<GetAIWaitingByTypeResponse>("/get-blog-ai-waiting", "POST", { blogAIId }, token);
};

export const getCartelAIWaiting = async ({
    token,
    cartelAIId,
}: GetCartelAIWaitingParams): Promise<GetCartelAIWaitingResponse> => {
    return fetchAPI<GetCartelAIWaitingResponse>("/get-cartel-ai-waiting", "POST", { cartelAIId }, token);
};

export const getNewsletterAIWaiting = async ({
    token,
    newsletterAIId,
}: GetNewsletterAIWaitingParams): Promise<GetNewsletterAIWaitingResponse> => {
    return fetchAPI<GetNewsletterAIWaitingResponse>("/get-newsletter-ai-waiting", "POST", { newsletterAIId }, token);
};

export const getWebsiteAIWaiting = async ({
    token,
    websiteAIId,
}: GetWebsiteAIWaitingParams): Promise<GetAIWaitingByTypeResponse> => {
    return fetchAPI<GetAIWaitingByTypeResponse>("/get-website-ai-waiting", "POST", { websiteAIId }, token);
};

export const getPressAIWaiting = async ({
    token,
    pressAIId,
}: GetPressAIWaitingParams): Promise<GetAIWaitingByTypeResponse> => {
    return fetchAPI<GetAIWaitingByTypeResponse>("/get-press-ai-waiting", "POST", { pressAIId }, token);
};

// Add cartel items to project
export const addCartelItemsToProject = async ({
    token,
    projectId,
    language,
    textItems,
}: {
    token: string;
    projectId: string;
    language: string;
    textItems: Array<{ title: string; text: string }>;
}): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/add-cartel-items-to-project", "POST", { projectId, language, textItems }, token);
};

// Add newsletter items to project
export const addNewsletterItemsToProject = async ({
    token,
    projectId,
    language,
    textItems,
}: {
    token: string;
    projectId: string;
    language: string;
    textItems: Array<{ title: string; text: string }>;
}): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>(
        "/add-newsletter-items-to-project",
        "POST",
        { projectId, language, textItems },
        token,
    );
};

// Update cartel items position in projects
export const updateCartelItemsPositionInProjects = async ({
    token,
    modifiedData,
}: UpdateAudioAIPositionParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/renumber-cartel-projects", "POST", { modifiedData }, token);
};

// Update newsletter items position in projects
export const updateNewsletterItemsPositionInProjects = async ({
    token,
    modifiedData,
}: UpdateAudioAIPositionParams): Promise<MessageResponse> => {
    return fetchAPI<MessageResponse>("/renumber-newsletter-projects", "POST", { modifiedData }, token);
};
